/**
 * usePhotostudioProjects Hook
 *
 * Provides CRUD operations for Photostudio projects and
 * saving images to projects.
 */

import { useCallback, useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type {
  IPhotostudioProject,
  IPhotostudioSettings,
  IPhotostudioReferences,
  IUsePhotostudioProjectsReturn,
} from '@/@types/photostudio';

export function usePhotostudioProjects(): IUsePhotostudioProjectsReturn {
  const [error, setError] = useState<string | null>(null);

  // Queries
  const projectsData = useQuery(api.photostudioProjects.getActiveProjects);
  const projects: IPhotostudioProject[] = (projectsData ?? []) as IPhotostudioProject[];

  // Mutations
  const createProjectMutation = useMutation(api.photostudioProjects.createProject);
  const updateProjectMutation = useMutation(api.photostudioProjects.updateProject);
  const deleteProjectMutation = useMutation(api.photostudioProjects.deleteProject);
  const saveImagesMutation = useMutation(api.photostudioImages.saveImagesToProject);

  // Loading state
  const isLoading = projectsData === undefined;

  // Create project
  const createProject = useCallback(
    async (
      name: string,
      description?: string
    ): Promise<{ success: boolean; projectId?: Id<'photostudioProjects'>; error?: string }> => {
      setError(null);

      try {
        const result = await createProjectMutation({
          name,
          description,
        });

        if (!result.success) {
          setError(result.error || 'Failed to create project');
        }

        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create project';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [createProjectMutation]
  );

  // Update project
  const updateProject = useCallback(
    async (
      projectId: Id<'photostudioProjects'>,
      name: string,
      description?: string
    ): Promise<{ success: boolean; error?: string }> => {
      setError(null);

      try {
        const result = await updateProjectMutation({
          projectId,
          name,
          description,
        });

        if (!result.success) {
          setError(result.error || 'Failed to update project');
        }

        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update project';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [updateProjectMutation]
  );

  // Delete project
  const deleteProject = useCallback(
    async (projectId: Id<'photostudioProjects'>): Promise<{ success: boolean; error?: string }> => {
      setError(null);

      try {
        const result = await deleteProjectMutation({ projectId });

        if (!result.success) {
          setError(result.error || 'Failed to delete project');
        }

        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete project';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [deleteProjectMutation]
  );

  // Save images to project
  const saveImagesToProject = useCallback(
    async (
      projectId: Id<'photostudioProjects'>,
      images: Array<{
        storageId: Id<'_storage'>;
        prompt: string;
        settings: IPhotostudioSettings;
        references: IPhotostudioReferences;
      }>
    ): Promise<{ success: boolean; error?: string }> => {
      setError(null);

      if (images.length === 0) {
        return { success: false, error: 'No images to save' };
      }

      try {
        const result = await saveImagesMutation({
          projectId,
          images,
        });

        if (!result.success) {
          setError(result.error || 'Failed to save images');
        }

        return { success: result.success, error: result.error };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to save images';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [saveImagesMutation]
  );

  return {
    projects,
    isLoading,
    error,
    createProject,
    updateProject,
    deleteProject,
    saveImagesToProject,
  };
}
